/** Compare organisation labels consistently without case or numeric surprises. */
export const compareOrganisationNames = (a: string, b: string): number =>
  a.localeCompare(b, 'en-GB', { numeric: true, sensitivity: 'base' });

/** Sort a copy, retaining every record and keeping equal names stable. */
export const sortOrganisations = <T>(
  items: readonly T[],
  getName: (item: T) => string | null | undefined,
): T[] => [...items].sort((a, b) => compareOrganisationNames(getName(a) ?? '', getName(b) ?? ''));

/** Keep children immediately below their parent, alphabetising each level. */
export const sortOrganisationHierarchy = <T extends {
  id: string;
  name: string;
  parent_organization_id?: string | null;
}>(items: readonly T[]): T[] => {
  const ids = new Set(items.map((item) => item.id));
  const roots = sortOrganisations(
    items.filter((item) => !item.parent_organization_id || !ids.has(item.parent_organization_id)),
    (item) => item.name,
  );
  const result: T[] = [];
  const visited = new Set<string>();
  const append = (item: T) => {
    if (visited.has(item.id)) return;
    visited.add(item.id);
    result.push(item);
    sortOrganisations(items.filter((child) => child.parent_organization_id === item.id), (child) => child.name)
      .forEach(append);
  };
  roots.forEach(append);
  // Retain malformed/cyclic records rather than silently losing options.
  sortOrganisations(items, (item) => item.name).forEach(append);
  return result;
};